import { DeepRequired } from '../types/DeepRequired';
import {
  inIframe,
  K3Extensions,
  pdfToDataUrl,
  useApp,
  useTriggerAction,
} from '@k3/core';
import { useIsKioskMode } from '../hooks/useIsKioskMode';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { SaveResult } from '@k3/core/dist/src/lib/types/configuration.interface';
import PrintIcon from '@mui/icons-material/Print';
import { useDealerTheme } from '../hooks/useDealerTheme';
import { useUrls } from '../hooks/useUrls';
import { getMessage } from './configurator/messages';

const PDFDialog = ({ result }: { result: SaveResult }) => {
  const [dataUrl, setDataUrl] = useState<string>('');
  const triggerAction = useTriggerAction();
  const { variableFontColor } = useDealerTheme();

  useEffect(() => {
    if (result.pdf) {
      const dataUrl = pdfToDataUrl(result.pdf);
      setDataUrl(dataUrl);
    }
  }, [result]);

  if (dataUrl) {
    return (
      <Dialog open={true} fullWidth maxWidth={'xl'}>
        <DialogTitle>
          <Typography sx={{ color: variableFontColor, fontWeight: 'bold' }}>
            Ihr Möbelstück
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ height: '90vh', overflow: 'hidden' }}>
          <iframe width={'100%'} height={'100%'} src={dataUrl} />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              // avoid memory leak
              URL.revokeObjectURL(dataUrl);
              setDataUrl('');
              triggerAction(null);
            }}
            data-cy="close"
          >
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
  return null;
};

export const ConfirmationDialog: DeepRequired<K3Extensions>['app']['orderDialog']['confirmation']['success']['root'] =
  (Wrapped) => (props) => {
    const kiosk = useIsKioskMode();
    const [pdfOpen, setPdfOpen] = useState<boolean>(false);
    const { saveParam, saveUrl } = useUrls();

    const url = useMemo(
      () =>
        `${saveUrl}?${inIframe() ? saveParam : 'code'}=${props.result.code}`,
      [props.result.code]
    );

    const app = useApp();
    const messageHeader = getMessage('pdfResultHeader', app.code);

    if (kiosk && props.result.pdf) {
      return (
        <Stack>
          <Typography fontWeight="bold">
            Ihre Konfigurations-ID: {props.result.code}
            <br />
            <br />
          </Typography>
          <Typography>
            {messageHeader} <br />
            <br />
          </Typography>
          <Typography>
            Ihre Konfiguration können Sie gleich hier ausdrucken. <br /> Mit dem
            Ausdruck oder Ihrer Konfigurations-ID können Sie den Kauf ganz
            einfach abschließen. Wenden Sie sich dazu einfach an den nächsten
            Mitarbeiter.
          </Typography>

          <Button
            startIcon={<PrintIcon />}
            variant="contained"
            onClick={() => setPdfOpen(true)}
            sx={{ gridColumn: '1 / -1', mt: 3 }}
          >
            Jetzt Ihre Konfiguration ausdrucken.
          </Button>
          {pdfOpen && <PDFDialog result={props.result} />}
        </Stack>
      );
    }

    return <Wrapped {...props} savedConfigurationUrl={url} />;
  };
